import getInteractiveChildren from '@/js/utils/getInteractiveChildren';
import getFirstAndLastItems from '@/js/utils/getFirstAndLastItems';

/**
 * PopOver component.
 */
export default class PopOver {
  /**
   * Get the target element based on the controller's `aria-controls` attribute.
   *
   * @param  {HTMLButtonElement} controller The component's controlling element.
   * @return {HTMLElement|null}
   */
  static getTargetElement(controller) {
    if (!controller.hasAttribute('aria-controls')) {
      const message = 'The component element is missing the required \'aria-controls\' attribute';
      throw new Error(`Configuration error: ${message}`);
    }

    const targetId = controller.getAttribute('aria-controls');
    const target = document.getElementById(targetId);

    if (target === null) {
      const message = `A target element with ID of '${targetId}' is not found`;
      throw new Error(`Configuration error: ${message}`);
    }

    return target;
  }

  /**
   * Start the component
   */
  constructor(controller, options = {}) {
    // Collect elements.
    this.controller = controller;
    this.target = this.constructor.getTargetElement(controller);

    // Bind class methods.
    this.init = this.init.bind(this);
    this.setState = this.setState.bind(this);
    this.stateWasUpdated = this.stateWasUpdated.bind(this);
    this.hide = this.hide.bind(this);
    this.show = this.show.bind(this);
    this.toggle = this.toggle.bind(this);
    this.handleTargetKeydown = this.handleTargetKeydown.bind(this);
    this.handleControllerKeydown = this.handleControllerKeydown.bind(this);
    this.hideOnOutsideClick = this.hideOnOutsideClick.bind(this);
    this.destroy = this.destroy.bind(this);

    /**
     * Component configuration options.
     *
     * @type {object}
     */
    const defaultOptions = {
      /**
       * The classname to add to the target when it is shown.
       *
       * @type {string}
       */
      shownClass: 'popover__target--is-shown',
    };

    // Merge options with defaults.
    this.options = Object.assign(defaultOptions, options);

    /**
     * Component state.
     *
     * @type {object}
     */
    this.state = {
      expanded: false,
    };

    this.init();
  }

  /**
   * Set up the component's DOM attributes and event listeners.
   */
  init() {
    // Collect the target element's interactive child elements.
    const interactiveChildElements = getInteractiveChildren(this.target);

    /*
     * Collect first and last interactive child elements from target and merge
     * them in as instance properties.
     */
    if (interactiveChildElements.length > 0) {
      const [
        firstInteractiveChild,
        lastInteractiveChild,
      ] = getFirstAndLastItems(interactiveChildElements);

      Object.assign(this, { firstInteractiveChild, lastInteractiveChild });
    }

    // Patch button role for non-button controller.
    if (this.controller.nodeName !== 'BUTTON') {
      this.controller.setAttribute('role', 'button');
    }

    // The button is collapsed by default.
    this.controller.setAttribute('aria-expanded', 'false');

    /*
     * Set the taget as hidden by default. Using the `aria-hidden` attribute,
     * rather than the `hidden` attribute, means authors must hide the target
     * element via CSS.
     */
    this.target.setAttribute('aria-hidden', 'true');

    // Add event listeners
    this.controller.addEventListener('click', this.toggle);
    this.controller.addEventListener('keydown', this.handleControllerKeydown);
    this.target.addEventListener('keydown', this.handleTargetKeydown);
    document.body.addEventListener('click', this.hideOnOutsideClick);
  }

  /**
   * Set component state.
   *
   * @param {object} newState The new state to merge with existing state.
   */
  setState(newState) {
    const updatedState = { ...this.state, ...newState };
    this.state = updatedState;

    this.stateWasUpdated(updatedState);
  }

  /**
   * Update the component attributes based on updated state.
   *
   * @param {object} state The component state.
   */
  stateWasUpdated({ expanded }) {
    const { shownClass } = this.options;
    this.controller.setAttribute('aria-expanded', `${expanded}`);

    // Update target attributes and activeElement.
    if (expanded) {
      this.target.setAttribute('aria-hidden', 'false');
      this.target.classList.add(shownClass);
    } else {
      this.target.setAttribute('aria-hidden', 'true');
      this.target.classList.remove(shownClass);
    }
  }

  /**
   * Handle keydown events on the target.
   *
   * @param {Event} event The event object.
   */
  handleTargetKeydown(event) {
    const { code, shiftKey } = event;
    const { expanded } = this.state;
    const { activeElement } = document;

    if (code === 'Escape' && expanded) {
      event.preventDefault();

      // Hide the target when the Escape key is pressed.
      this.hide();

      // Move focus to the controller.
      this.controller.focus();
    }

    /*
     * Close when tabbing from the last item.
     */
    if (
      code === 'Tab'
      && !shiftKey
      && this.lastInteractiveChild === activeElement
    ) {
      this.hide();
    }
  }

  /**
   * Handle keydown events on the Popover controller.
   *
   * @param {Event} event The event object.
   */
  handleControllerKeydown(event) {
    const { code } = event;
    const { expanded } = this.state;

    if (['Space', 'Enter'].includes(code)) {
      event.preventDefault();

      /*
       * Treat the Spacebar and Return keys as clicks in case the controller is
       * not a <button>.
       */
      this.toggle(event);
    }

    if (code === 'Escape' && expanded) {
      event.preventDefault();

      // Hide the target when the Escape key is pressed.
      this.hide();
    }
  }

  /**
   * Close the target when clicking anywhere outside of the target or controller
   * elements.
   *
   * @param {Event} event The event object.
   */
  hideOnOutsideClick(event) {
    const { expanded } = this.state;
    const { target: clicked } = event;

    if (
      expanded
      && clicked !== this.controller
      && !this.target.contains(clicked)
    ) {
      this.hide();
    }
  }

  /**
   * Update component state to show the target element.
   */
  show() {
    this.setState({ expanded: true });
  }

  /**
   * Update component state to hide the target element.
   */
  hide() {
    this.setState({ expanded: false });
  }

  /**
   * Toggle component state.
   *
   * @param {Event} event The Event object.
   */
  toggle(event) {
    event.preventDefault();
    const { expanded } = this.state;

    this.setState({ expanded: !expanded });
  }

  /**
   * Remove all attributes and event listeners added by this class.
   */
  destroy() {
    const { shownClass } = this.options;

    // Remove target attributes.
    this.target.removeAttribute('aria-hidden');
    this.target.classList.remove(shownClass);

    // Remove controller attributes.
    this.controller.removeAttribute('aria-expanded');
    if (this.controller.nodeName !== 'BUTTON') {
      this.controller.removeAttribute('role');
    }

    // Remove event listeners.
    this.controller.removeEventListener('keydown', this.handleControllerKeydown);
    this.controller.removeEventListener('click', this.toggle);
    this.target.removeEventListener('keydown', this.handleTargetKeydown);

    // Reset initial state.
    this.state = {
      expanded: false,
    };
  }
}
